import { IonCol, IonGrid, IonList, IonRow } from '@ionic/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { transformPhoneNumber } from '../../../../utilities'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getEmployerSelector } from '../selectors'
import { useSelector } from 'react-redux'
import { getPatientDemographicsOptionsSelector, getemployerOptionsSelector } from '../../../options/selectors'
import { ICity, ICountyOption, IOption } from '../../../../common/common.types'
import {
  getEmployerCountyOptions,
  setAddress1,
  setAddress2,
  setCity,
  setCountry,
  setCounty,
  setIndustry,
  setOccupation,
  setPhoneNumber,
  setPhoneType,
  setPrimaryBusiness,
  setState,
  setZipCode,
} from '../store/employer.actions'
import SInput from '../../../../components/stringInput/StringInput.component'
import { EmployerConstants } from '../store/employer.constants'
import Validation from '../../../../components/validation/Validation.component'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import { zipCodeKeys } from '../../../../components/numberPad/NumberPad.types'
import { setZipState } from '../../../../components/zip/store/zip.actions'
import { getPatienteEmployerCityNemsisCode } from '../../../locationSearch/store/locationSearch.actions'
import ValidationMessage from '../../../../components/validation/ValidationMessage.component'
import { getLocationSearchState } from '../../../locationSearch/selectors'
import { ILocationSearchState } from '../../../locationSearch/store/locationSearch.types'

const Employer: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const employer = useSelector(getEmployerSelector)
  const {
    industry,
    occupation,
    primaryBusiness,
    address1,
    address2,
    city,
    state,
    county,
    zipCode,
    country,
    phoneNumber,
    phoneType,
  } = employer
  const employerOptions = useSelector(getemployerOptionsSelector)
  const { industryOptions, occupationOptions, employerCountyOptions } = employerOptions
  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const { stateOptions, countryOptions, phoneTypeOptions } = patientDemographicsOptions
  const [isEmployerCountyEnabled, setIsEmployerCountyEnabled] = useState(false)
  const { errorMsgsMap, enableLocationSearch }: ILocationSearchState = useSelector(getLocationSearchState)
  const { isTablet } = useDeviceMetrics()

  const setIndustryOnChange = (option: IOption) => {
    dispatch(setIndustry(option))
  }

  const setOccupationOnChange = (option: IOption) => {
    dispatch(setOccupation(option))
  }

  const setPrimaryBusinessOnChange = (e: CustomEvent) => {
    e.preventDefault()
    if (e.detail.value !== primaryBusiness) dispatch(setPrimaryBusiness(e.detail.value))
  }

  const setAddress1OnChange = (e: CustomEvent) => {
    e.preventDefault()
    if (e.detail.value !== address1) dispatch(setAddress1(e.detail.value))
  }

  const setAddress2OnChange = (e: CustomEvent) => {
    e.preventDefault()
    if (e.detail.value !== address2) dispatch(setAddress2(e.detail.value))
  }

  const setCityOnChange = (e: CustomEvent) => {
    e.preventDefault()
    const updatedCity: ICity = { name: e.detail.value, nemsisCode: '' }
    if (e.detail.value === city?.name && city?.nemsisCode) {
      updatedCity.nemsisCode = city.nemsisCode
    }
    dispatch(setCity(updatedCity))
  }

  const setStateOnChange = (option: IOption) => {
    if (county?.state !== option?.id) {
      dispatch(setCounty(''))
    }

    dispatch(setState(option))
  }

  const setCountyOnChange = (option: ICountyOption) => {
    dispatch(setCounty(option))
  }

  const setZipCodeOnChange = (numbers: string[]) => {
    dispatch(setZipCode(numbers.join('')))
    if (numbers.length === 5) {
      dispatch(setZipState(numbers.join(''), EmployerConstants.SET_CITY, EmployerConstants.SET_STATE))
    }
  }

  const setCountryOnChange = (option: IOption) => {
    dispatch(setCountry(option))
  }

  const setPhoneNumberOnChange = (numbers: string[]) => {
    const newPhoneNumber = numbers.length ? transformPhoneNumber(numbers.join('')) : ''
    dispatch(setPhoneNumber(newPhoneNumber))
  }

  const setPhoneTypeOnChange = (option: IOption) => {
    dispatch(setPhoneType(option))
  }

  const handleCountyControlBasedOnEmployerState = () => {
    const enableEmployerCounty = state ? true : false

    if (enableEmployerCounty) {
      dispatch(getEmployerCountyOptions())
    }

    setIsEmployerCountyEnabled(enableEmployerCounty)
  }

  useEffect(() => {
    primaryBusiness && dispatch(setPrimaryBusiness(primaryBusiness))
    address1 && dispatch(setAddress1(address1))
    address2 && dispatch(setAddress2(address2))
    city && dispatch(setCity(city))
    zipCode && dispatch(setZipCode(zipCode))
    phoneNumber && dispatch(setPhoneNumber(phoneNumber))
  }, [primaryBusiness, address1, address2, city, zipCode, phoneNumber, dispatch])

  useEffect(() => {
    handleCountyControlBasedOnEmployerState()
  }, [state])

  return (
    <>
      <IonList id='employerContent' className={isTablet ? 'ion-margin-horizontal' : ''}>
        <IonGrid className='no-padding'>
          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='Industry'
                selectedOption={industry}
                listHeader={t('patientDemographics.employer.Industry')}
                label={t('patientDemographics.employer.Industry')}
                options={industryOptions}
                action={setIndustryOnChange}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='Occupation'
                selectedOption={occupation}
                listHeader={t('patientDemographics.employer.Occupation')}
                label={t('patientDemographics.employer.Occupation')}
                options={occupationOptions}
                action={setOccupationOnChange}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.employer.PrimaryBusiness')}
                dataTestID='PrimaryBusiness'
                inputValue={primaryBusiness}
                onChangeAction={setPrimaryBusinessOnChange}
              />
              <Validation id={EmployerConstants.SET_PRIMARY_BUSINESS} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.employer.Address1')}
                dataTestID='EmployerAddress1'
                inputValue={address1}
                onChangeAction={setAddress1OnChange}
              />
              <Validation id={EmployerConstants.SET_ADDRESS1} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.employer.Address2')}
                dataTestID='EmployerAddress2'
                inputValue={address2}
                onChangeAction={setAddress2OnChange}
              />
              <Validation id={EmployerConstants.SET_ADDRESS2} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                isPHI
                elementId='EmployerZipCode'
                dataTestId='EmployerZipCode'
                values={zipCode ? zipCode.toString().split('') : []}
                label={t('patientDemographics.employer.ZipCode')}
                onNumPadClick={setZipCodeOnChange}
                validCharacters={zipCodeKeys}
              />
              <Validation id={EmployerConstants.SET_ZIPCODE} />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('patientDemographics.employer.City')}
                dataTestID='EmployerCity'
                inputValue={city?.name || ''}
                onChangeAction={setCityOnChange}
                onBlurAction={() => dispatch(getPatienteEmployerCityNemsisCode())}
              />
              <Validation id={EmployerConstants.SET_CITY} />
              {enableLocationSearch && (
                <ValidationMessage
                  id={`${EmployerConstants.SET_CITY}_ERROR`}
                  message={errorMsgsMap[EmployerConstants.SET_CITY]}
                />
              )}
            </IonCol>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='EmployerState'
                selectedOption={state}
                listHeader={t('patientDemographics.employer.State')}
                label={t('patientDemographics.employer.State')}
                options={stateOptions}
                action={setStateOnChange}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                isPHI
                listHeader={t('patientDemographics.employer.County')}
                label={t('patientDemographics.employer.County')}
                dataTestID='EmployerCounty'
                options={employerCountyOptions}
                selectedOption={county}
                action={setCountyOnChange}
                disabled={!isEmployerCountyEnabled}
              />
              {enableLocationSearch && (
                <ValidationMessage
                  id={`${EmployerConstants.SET_COUNTY}_ERROR`}
                  message={errorMsgsMap[EmployerConstants.SET_COUNTY]}
                />
              )}
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='EmployerCountry'
                selectedOption={country}
                listHeader={t('patientDemographics.employer.Country')}
                label={t('patientDemographics.employer.Country')}
                options={countryOptions}
                action={setCountryOnChange}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                isPHI
                elementId={'EmployerPhoneNumber'}
                dataTestId={'EmployerPhoneNumber'}
                values={phoneNumber ? phoneNumber.toString().split('') : []}
                label={t('patientDemographics.employer.PhoneNumber')}
                onNumPadClick={(numbers) => setPhoneNumberOnChange(numbers)}
              />
              <Validation id={EmployerConstants.SET_PHONENUMBER} />
            </IonCol>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='EmployerPhoneType'
                selectedOption={phoneType}
                listHeader={t('patientDemographics.employer.PhoneType')}
                label={t('patientDemographics.employer.PhoneType')}
                options={phoneTypeOptions}
                action={setPhoneTypeOnChange}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonList>
    </>
  )
}

export default Employer
